import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
} from '@mui/material';
import {
  Inbox as InboxIcon,
  Done as DoneIcon,
  Archive as ArchiveIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { NotificationType, NotificationPriority } from '../../types/user';

export interface NotificationFilterState {
  readStatus: 'all' | 'unread' | 'read';
  archived: 'active' | 'archived' | 'all';
  type?: NotificationType;
  priority?: NotificationPriority;
}

interface NotificationFiltersProps {
  filters: NotificationFilterState;
  onChange: (filters: NotificationFilterState) => void;
  counts?: {
    all: number;
    unread: number;
    archived: number;
  };
}

/**
 * NotificationFilters Component
 * Provides filtering options for the notification center
 */
export const NotificationFilters: React.FC<NotificationFiltersProps> = ({
  filters,
  onChange,
  counts,
}) => {
  const handleReadStatusChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: 'all' | 'unread' | 'read' | null
  ) => {
    if (newValue !== null) {
      onChange({ ...filters, readStatus: newValue });
    }
  };

  const handleArchivedChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: 'active' | 'archived' | 'all' | null
  ) => {
    if (newValue !== null) {
      onChange({ ...filters, archived: newValue });
    }
  };

  const handleTypeChange = (event: any) => {
    const value = event.target.value;
    onChange({
      ...filters,
      type: value === 'all' ? undefined : (value as NotificationType),
    });
  };

  const handlePriorityChange = (event: any) => {
    const value = event.target.value;
    onChange({
      ...filters,
      priority: value === 'all' ? undefined : (value as NotificationPriority),
    });
  };

  // Common notification types for filter dropdown
  const commonTypes: NotificationType[] = [
    'welcome',
    'approval_granted',
    'approval_denied',
    'content_approved',
    'content_rejected',
    'comment_mention',
    'comment_reply',
    'research_note_assigned',
    'birthday_reminder',
    'system_announcement',
  ];

  return (
    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Stack spacing={3}>
        {/* Read Status Section */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}>
            Status
          </Typography>
          <Stack spacing={1}>
            <ToggleButtonGroup
              value={filters.readStatus}
              exclusive
              onChange={handleReadStatusChange}
              size="small"
              orientation="vertical"
              fullWidth
              sx={{
                '& .MuiToggleButton-root': {
                  justifyContent: 'flex-start',
                  px: 1.5,
                  py: 1,
                  textAlign: 'left',
                },
              }}
            >
              <ToggleButton value="all">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InboxIcon fontSize="small" />
                    <span>All</span>
                  </Box>
                  {counts && counts.all > 0 && (
                    <Chip label={counts.all} size="small" sx={{ height: 20 }} />
                  )}
                </Box>
              </ToggleButton>
              <ToggleButton value="unread">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StarIcon fontSize="small" />
                    <span>Unread</span>
                  </Box>
                  {counts && counts.unread > 0 && (
                    <Chip
                      label={counts.unread}
                      size="small"
                      color="primary"
                      sx={{ height: 20 }}
                    />
                  )}
                </Box>
              </ToggleButton>
              <ToggleButton value="read">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DoneIcon fontSize="small" />
                  <span>Read</span>
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Box>

        {/* Archive Section */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}>
            Archive
          </Typography>
          <ToggleButtonGroup
            value={filters.archived}
            exclusive
            onChange={handleArchivedChange}
            size="small"
            orientation="vertical"
            fullWidth
            sx={{
              '& .MuiToggleButton-root': {
                justifyContent: 'flex-start',
                px: 1.5,
                py: 1,
                textAlign: 'left',
              },
            }}
          >
            <ToggleButton value="active">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InboxIcon fontSize="small" />
                <span>Active</span>
              </Box>
            </ToggleButton>
            <ToggleButton value="archived">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ArchiveIcon fontSize="small" />
                  <span>Archived</span>
                </Box>
                {counts && counts.archived > 0 && (
                  <Chip label={counts.archived} size="small" sx={{ height: 20 }} />
                )}
              </Box>
            </ToggleButton>
            <ToggleButton value="all">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InboxIcon fontSize="small" />
                <span>All</span>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Type Filter */}
        <FormControl fullWidth size="small">
          <InputLabel>Type</InputLabel>
          <Select
            value={filters.type || 'all'}
            label="Type"
            onChange={handleTypeChange}
          >
            <MenuItem value="all">All Types</MenuItem>
            {commonTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Priority Filter */}
        <FormControl fullWidth size="small">
          <InputLabel>Priority</InputLabel>
          <Select
            value={filters.priority || 'all'}
            label="Priority"
            onChange={handlePriorityChange}
          >
            <MenuItem value="all">All Priorities</MenuItem>
            <MenuItem value={NotificationPriority.CRITICAL}>Critical</MenuItem>
            <MenuItem value={NotificationPriority.HIGH}>High</MenuItem>
            <MenuItem value={NotificationPriority.NORMAL}>Normal</MenuItem>
            <MenuItem value={NotificationPriority.LOW}>Low</MenuItem>
          </Select>
        </FormControl>
      </Stack>
    </Box>
  );
};
